import * as L from 'leaflet';
import { GeoJsonObject, Feature, GeometryObject, Point } from 'geojson';
import { MapLayer } from '../mapLayer.abstract';
import { MarkerClusterOptions, MarkerLayer } from '../markerLayer';
import { FaMarker } from '../../marker/faMarker/faMarker';
import { BgsLeafletComponent } from '../../bgsLeaflet.component';
import { GeoJsonLayerFeatureItemGenerator } from './geoJsonLayerFeatureItemGenerator';
import { LayerWithMarkers } from '../layerWithMarkers.interface';
class GeoJsonMarkerLayer extends MarkerLayer {
  public setMapObject(bgsLeaflet: BgsLeafletComponent): void {
    this.bgsLeaflet = bgsLeaflet;
  }
}

export class GeoJsonLayer extends MapLayer implements LayerWithMarkers {
  protected geoJsonData: GeoJsonObject; // Geojson data
  protected dataPromise: Promise<void>;
  protected markerLayer: GeoJsonMarkerLayer;
  protected geoLayer: null | L.GeoJSON;

  protected getDataFunc: () => Promise<GeoJsonObject>;

  // http://leafletjs.com/reference-1.3.0.html#layer-bindpopup
  protected featureDisplayContentFunc: (feature: Feature<GeometryObject, Record<string, unknown>>) => string;
  // http://leafletjs.com/reference-1.3.0.html#layer-bindpopup
  protected tooltipFunction: (feature: Feature<GeometryObject, Record<string, unknown>>) => string;

  constructor(id: string, name: string) {
    super(id, name);
    // Default options
    this.options.setOptions({
      pane: 'geoJsonPane',
    });

    this.markerLayer = new GeoJsonMarkerLayer(this.id + '_markers');
  }

  public getLeafletLayer(): Promise<null | L.Layer> {
    // console.debug('getLeafletLayer', this.id, this.geoJsonData);
    return this.populateData().then(() => this.createLayer(this.geoJsonData));
  }

  public setClusteredMarkers(
    clustered = true,
    clusterOptions = new MarkerClusterOptions(),
    enableClusterClickFeatureIdentify = false,
    customClusterClickFunc?: (markers: Array<L.Marker>, clickEvent: L.LeafletMouseEvent) => void,
  ): this {
    this.markerLayer.setClustered(clustered, clusterOptions, enableClusterClickFeatureIdentify, customClusterClickFunc);
    return this;
  }

  public setGetDataFunction(getDataFunc: () => Promise<GeoJsonObject>, callImmediately = true): this {
    this.getDataFunc = getDataFunc;
    if (callImmediately) {
      void this.populateData();
    }
    return this;
  }

  public setGeoJsonData(data: GeoJsonObject): this {
    this.geoJsonData = data;
    return this;
  }

  public setStylingFunction(func: () => L.PathOptions): this {
    this.stylingFunction = func;
    return this;
  }
  public setFeatureDisplayContentFunc(
    func: (feature: Feature<GeometryObject, Record<string, unknown>>) => string,
  ): this {
    this.featureDisplayContentFunc = func;
    return this;
  }
  public setFeatureContentClickFunc(
    func: (event: MouseEvent, feature: Feature<GeometryObject, Record<string, unknown>>) => void,
  ): this {
    this.featureContentClickFunction = func;
    return this;
  }

  public setTooltipFunction(func: (feature: Feature<GeometryObject, Record<string, unknown>>) => string): this {
    this.tooltipFunction = func;
    return this;
  }

  public setPointToLayerFunction(
    func: null | ((geoJsonPoint: Feature<Point, Record<string, unknown>>, latlng: L.LatLng) => L.Layer),
  ): this {
    // console.debug('setPointToLayerFunction', func);
    this.pointToLayerFunction = func ? func : (a, b) => this.defaultPointToLayerFunction(a, b);
    return this;
  }

  public setZOffset(index?: number): void {
    if (null != this.markerLayer) {
      this.markerLayer.setZOffset(index);
    }
  }

  protected stylingFunction(): L.PathOptions {
    return {} as L.PathOptions;
  }
  protected pointToLayerFunction(geoJsonPoint: Feature<Point, Record<string, unknown>>, latlng: L.LatLng): L.Layer {
    return this.defaultPointToLayerFunction(geoJsonPoint, latlng);
  }

  protected featureContentClickFunction: (
    event: MouseEvent,
    feature: Feature<GeometryObject, Record<string, unknown>>,
  ) => void = () => null;

  protected createLayer(geoJsonData: GeoJsonObject): null | Promise<L.Layer> {
    this.setLayerClickFeatureItemGenerator(
      this.featureDisplayContentFunc
        ? new GeoJsonLayerFeatureItemGenerator(
          this,
          geoJsonData,
          this.featureDisplayContentFunc,
          this.featureContentClickFunction,
        )
        : null,
    );

    // console.debug('createLayer', this.id, geoJsonData);
    this.markerLayer.setMapObject(this.bgsLeaflet);
    this.markerLayer.clearMarkers();
    this.geoLayer = null;
    if (geoJsonData != null) {
      try {
        this.geoLayer = L.geoJSON(geoJsonData, {
          ...this.options.getAll(),
          style: {
            ...this.stylingFunction(),
            className: 'bgs-leaflet-geojson',
          },
          onEachFeature: (feature: Feature<GeometryObject, Record<string, unknown>>, layer: L.Layer) => {
            const tooltip = this.generateTooltip(feature);
            if (tooltip != null) {
              layer.bindTooltip(tooltip);
            }
          },
          pointToLayer: (geoJsonPoint: Feature<Point, Record<string, unknown>>, latlng: L.LatLng): null | L.Layer => {
            let layer: null | L.Layer = this.pointToLayerFunction(geoJsonPoint, latlng);
            // if it's a marker remove it and to our own marker layer,
            //  so that we can optionally cluster
            if (layer instanceof L.Marker) {
              const featureHtml = this.generateFeatureHtml(geoJsonPoint);
              // console.debug('featureHtml', featureHtml);
              const tooltip = this.generateTooltip(geoJsonPoint);
               
              const markerId = String(layer['id'] != null ? layer['id'] : this.markerLayer.getMarkers().length);
              this.markerLayer.addLeafletMarker(markerId, layer, tooltip, featureHtml, (event) =>
                this.featureContentClickFunction(event, geoJsonPoint),
              );
              layer = null; // clear it from geo layer
            }
            return layer;
          },
        } as L.GeoJSONOptions);
      } catch (error) {
        console.warn(error);
        this.geoLayer = null;
      }
    }
    return (this.geoLayer == null)
      ? null
      : this.markerLayer.getLeafletLayer().then((leafletMarkerLayer: L.Layer) => {
        return L.layerGroup([this.geoLayer as L.Layer, leafletMarkerLayer]);
      });
  }

  // override for layer specific operations
  protected preLayerAdd(): Promise<void> {
    return Promise.resolve().then(() => this.ensurePaneExists(this.options.pane.get(), 460));
  }

  protected populateData(): Promise<void> {
    if (this.geoJsonData != null) {
      return Promise.resolve();
    } else if (this.getDataFunc != null) {
      if (this.dataPromise == null) {
        // console.debug('populating Data');
        this.dataPromise = this.getDataFunc()
          .then((data: GeoJsonObject) => {
            // console.debug('got Data');
            this.geoJsonData = data;
          })
          .catch(() => {
            console.warn(`Failed to populate GeoJson data of '${this.name}' layer.`);
          });
      }
      return this.dataPromise;
    } else {
      this.geoJsonData = {
        type: 'FeatureCollection',
        features: [],
      } as GeoJsonObject;
      return Promise.resolve();
    }
  }

  protected generateFeatureHtml(feature: Feature<GeometryObject, Record<string, unknown>>): null | HTMLElement {
    let element: null | HTMLElement = null;
    if (this.featureDisplayContentFunc) {
      let value: null | string = this.featureDisplayContentFunc(feature);
      if (!/\S+/.test(value)) {
        value = null;
      }
      if (null != value) {
        element = document.createElement('div');
        element.innerHTML = value;
      }
    }
    return element;
  }
  protected generateTooltip(feature: Feature<GeometryObject, Record<string, unknown>>): null | string | HTMLElement {
    let value: null | string | HTMLElement = null;

    if (this.tooltipFunction) {
      value = this.tooltipFunction(feature);
      if (typeof value === 'string' && !/\S+/.test(value)) {
        value = null;
      }
    }
    return value;
  }
  protected defaultPointToLayerFunction(
    geoJsonPoint: Feature<Point, Record<string, unknown>>,
    latlng: L.LatLng,
  ): L.Layer {
    const icon = new FaMarker();
    return L.marker(latlng, { icon: icon } as L.MarkerOptions);
  }

  protected updateLeafletLayerOpacity(): void {
    // console.debug('updateLeafletLayerOpacity');
    const opacity = this.options.opacity.get()!;
    if (this.geoLayer != null && typeof this.geoLayer.setStyle === 'function') {
      const style = {
        opacity: opacity,
        fillOpacity: Math.min(opacity, 0.2),
      } as L.PathOptions;
      // console.debug('call setStyle');
      this.geoLayer.setStyle(style);
    }
    if (this.markerLayer != null) {
      this.markerLayer.options.opacity.set(opacity);
    }
  }
  // push these through to the geo layer
  protected bringLayerToFront(refIndex: number): void {
    this.setZOffset(refIndex);
    if (this.geoLayer != null) {
      this.geoLayer.bringToFront();
    }
  }
  protected bringLayerToBack(refIndex: number): void {
    this.setZOffset(refIndex);
    if (this.geoLayer != null) {
      this.geoLayer.bringToBack();
    }
  }
}
